import { Injectable, inject } from '@angular/core';
import { WA_WINDOW } from '@ng-web-apis/common';
import { nonNullable } from '@rma/common-ui-rxjs/operators';
import { DynamicAssetLoaderService, DynamicLoaderAssets } from '@rma/generic/util/dynamic-asset-loader';
import { PlatformService } from '@rma/generic/util/environment';
import { BehaviorSubject, Observable, map } from 'rxjs';

interface NgRecaptchaWindow extends Window {
  ng2recaptchaloaded: () => void;
}

@Injectable({
  providedIn: 'root',
})
export class RmaRecaptchaLoaderService {
  private readonly dynamicAssetLoader = inject(DynamicAssetLoaderService);
  private readonly platformService = inject(PlatformService);
  private readonly window = inject<NgRecaptchaWindow>(WA_WINDOW);

  public isLoaded$: Observable<boolean>;
  public ready: Observable<ReCaptchaV2.ReCaptcha>;
  public language = '';
  private readySource = new BehaviorSubject<ReCaptchaV2.ReCaptcha | null>(null);

  constructor() {
    this.ready = this.readySource.asObservable().pipe(nonNullable());
    this.isLoaded$ = this.readySource.asObservable().pipe(map((v) => !!v));
  }

  public updateLanguage(newLang = 'en'): void {
    if (newLang === this.language) {
      return;
    }

    this.language = newLang;
    this.readySource.next(null);
    this.init();
  }

  public load(lang = 'en'): void {
    this.language = lang;
    this.init();
  }

  private init() {
    if (this.readySource?.value) {
      return;
    }

    if (this.platformService.isPlatformServer) {
      this.readySource.next(null);
      return;
    }

    this.dynamicAssetLoader.appendAsset({ asset: DynamicLoaderAssets.ReCaptcha, token: this.language });

    this.window.ng2recaptchaloaded = () => {
      this.readySource.next(grecaptcha);
    };
  }
}
